import React from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import numeral from 'numeral';

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';

import Scrollbar from '../../../components/Scrollbar';
import { TableNoData } from '../../../components/table';
import TableRowCellsFromHeader from '../../../components/table/TableRowCellsFromHeader';

import useLocales from '../../../hooks/useLocales';

// Fuel OperationType ids (seeded once per tenant, identical across tenants)
export const FT_OP_FILL = 12;
export const FT_OP_REFILL = 13;
export const FT_OP_ADJUST = 14;
export const FT_OP_GAS = 15;   // Gas station fill (outside the farm, no tank)
export const FT_OP_EV = 16;    // EV charge (outside/in the farm, no tank)

// Operation types that are "external" (not linked to a farm tank)
export const FT_EXTERNAL_OPS = [FT_OP_GAS, FT_OP_EV];

export const operationTypeLabelKey = (id) => {
    if (Number(id) === FT_OP_FILL) return 'refillVehicle';
    if (Number(id) === FT_OP_REFILL) return 'refillTank';
    if (Number(id) === FT_OP_ADJUST) return 'adjustReading';
    if (Number(id) === FT_OP_GAS) return 'gasStationFill';
    if (Number(id) === FT_OP_EV) return 'evCharge';
    return '';
};

// Hardcoded enums (not synced tables) - mirror the app / API.
export const FT_PAYMENT_CARD = 1;
export const FT_PAYMENT_CASH = 2;
export const FT_PAYMENT_FLEET = 3;
export const paymentLabelKey = (id) => {
    if (Number(id) === FT_PAYMENT_CARD) return 'paymentCard';
    if (Number(id) === FT_PAYMENT_CASH) return 'paymentCash';
    if (Number(id) === FT_PAYMENT_FLEET) return 'paymentFleetCard';
    return '';
};

export const FT_EV_FARM = 1;
export const FT_EV_PUBLIC = 2;
export const FT_EV_PRIVATE = 3;
export const evLocationLabelKey = (id) => {
    if (Number(id) === FT_EV_FARM) return 'evWhereFarm';
    if (Number(id) === FT_EV_PUBLIC) return 'evWherePublic';
    if (Number(id) === FT_EV_PRIVATE) return 'evWherePrivate';
    return '';
};

const num = (v) => (v == null ? '-' : String(Math.round(Number(v))));

// Quantities (litres / kWh / cost) keep up to 2 decimals and use one shared formatter,
// so the table and the detail modal always render the same way (no 78,99 vs 78.99,
// no 52.4 L silently rounded to 52).
export const fmtQty = (v) => (v == null || v === '' ? '-' : numeral(Number(v)).format('0,0.[00]'));
const withUnit = (v, u) => {
    const s = fmtQty(v);
    return s === '-' ? '-' : `${s} ${u}`;
};

// The "volume" column is overloaded per operation type: ADJUST shows the resulting
// level (L), EV shows energy (kWh), everything else shows litres moved/added (L).
// The unit is shown so litres and kWh are never confused in the same column.
export const volumeLabelFor = (row) => {
    const type = Number(row.operationTypeId);
    if (type === FT_OP_ADJUST) return withUnit(row.newLitres, 'L');
    if (type === FT_OP_EV) return withUnit(row.energyKwh, 'kWh');
    return withUnit(row.volume, 'L');
};

const fmtDate = (ms) => (ms ? format(new Date(Number(ms)), 'dd/MM/yyyy') : '-');
const fmtHour = (ms) => (ms ? format(new Date(Number(ms)), 'HH:mm') : '-');

FTRecentOperationsTable.propTypes = {
    data: PropTypes.array,
    showFuelTank: PropTypes.bool,
    hiddenColumns: PropTypes.array,
};

export default function FTRecentOperationsTable({ data = [], showFuelTank = false, hiddenColumns = [] }) {
    const { translate } = useLocales();

    const columns = [
        { id: 'date', label: 'date', show: true, align: 'left' },
        { id: 'hour', label: 'hour', show: true, align: 'center' },
        ...(showFuelTank ? [{ id: 'fuelTankName', label: 'fuelTank', show: true, align: 'center' }] : []),
        { id: 'operationTypeLabel', label: 'operationType', show: true, align: 'center' },
        { id: 'machineName', label: 'vehicle', show: true, align: 'center' },
        { id: 'volumeLabel', label: 'volume', show: true, align: 'center' },
        { id: 'counterBefore', label: 'tankReadingBefore', show: true, align: 'center' },
        { id: 'counterAfter', label: 'tankReadingAfter', show: true, align: 'center' },
        { id: 'machineCounter', label: 'vehicleReading', show: true, align: 'center' },
        { id: 'newCounter', label: 'newReading', show: true, align: 'center' },
        { id: 'newLitres', label: 'newLevel', show: true, align: 'center' },
        { id: 'supplier', label: 'supplier', show: true, align: 'center' },
        { id: 'guideNumber', label: 'guideNumber', show: true, align: 'center' },
        { id: 'username', label: 'user', show: true, align: 'center' },
        { id: 'notes', label: 'notes', show: true, align: 'center' },
    ].filter((c) => !hiddenColumns.includes(c.id));

    const rows = data.map((row) => ({
        ...row,
        date: fmtDate(row.date),
        hour: fmtHour(row.createdDt),
        fuelTankName: row.fuelTankName || '-',
        operationTypeLabel: translate(operationTypeLabelKey(row.operationTypeId)),
        machineName: row.machineName || '-',
        volumeLabel: volumeLabelFor(row),
        counterBefore: num(row.counterBefore),
        counterAfter: num(row.counterAfter),
        machineCounter: num(row.machineCounter),
        newCounter: num(row.newCounter),
        newLitres: num(row.newLitres),
        supplier: row.supplier || '-',
        guideNumber: row.guideNumber || '-',
        username: row.username || '-',
        notes: row.notes || '-',
    }));

    return (
        <Scrollbar>
            <Table size="small" sx={{ minWidth: 900 }}>
                <TableHead>
                    <TableRow>
                        {columns.map((c) => (
                            <TableCell key={c.id} align={c.align}>{translate(c.label)}</TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, i) => (
                        <TableRow hover key={row.id ?? i}>
                            <TableRowCellsFromHeader headers={columns} row={row} />
                        </TableRow>
                    ))}
                    <TableNoData isNotFound={!data.length} />
                </TableBody>
            </Table>
        </Scrollbar>
    );
}
